import React from "react";
import type {GridColDef, GridRenderCellParams} from "@mui/x-data-grid-premium";
import {Box, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "~/constants";
import {OpportunityRankPopoverText} from "~/help/OpportunityRankPopoverText";
import type {AdvancedSupplierBreakdownData} from "~/services/ApiTypes";
import type {BusinessUnit} from "~/services/classes/MaterializedClasses";
import {getSupplierIdHack} from "~/stores/hacks/display";
import {CountryFlag} from "~/pages/spend-landscape/components/CountryFlag";
import {OpportunityRankCell} from "./OpportunityRankCell";
import {SpendBarCell} from "./SpendBarCell";
import {ContextMenuAction} from "./ContextMenuAction";

/** What decides which columns a table has, from the customer profile and the dataset */
export type ColumnContext = {
    showL4: boolean
    hideParents: boolean
    /** Rows are grouped by parent supplier (the "By parent supplier" switch) */
    byParent: boolean
    parentLabel: string
    showSupplierId: boolean
    showBusinessUnits: boolean
    buLabel: string
    showCDescription: boolean
    cDescriptionLabel: string
    showSource: boolean
    showDatasetName: boolean
    isSynergy: boolean
    getBagName: (databagId: number) => string
}

const categoryValue = (v: unknown) => v === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : (v as string) ?? '';

function categoryColumns(ctx: ColumnContext, sortable: boolean): GridColDef[] {
    const levels = ctx.showL4 ? [1, 2, 3, 4] : [1, 2, 3];
    return levels.map(level => ({
        field: `active_l${level}`,
        headerName: `L${level}`,
        flex: 1,
        minWidth: 140,
        sortable,
        valueFormatter: categoryValue,
        renderCell: level === levels.length
            ? (p: GridRenderCellParams) => <Typography variant="body2" fontWeight={600} noWrap title={categoryValue(p.value)}>
                {categoryValue(p.value)}
            </Typography>
            : undefined,
    }));
}

function currencyColumn(field: string, headerName: string, sortable = true): GridColDef {
    return {
        field, headerName, type: 'number', width: 140, sortable, align: 'right', headerAlign: 'right',
        renderCell: p => <span className="tabular"><CurrencyComponent v={Number(p.value ?? 0)}/></span>,
    };
}

export function opportunityColumns(ctx: ColumnContext): GridColDef[] {
    return [
        ...categoryColumns(ctx, true),
        {
            field: 'opportunity_rank',
            headerName: 'Opportunity rank',
            width: 170,
            renderHeader: () => <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <span className="MuiDataGrid-columnHeaderTitle">Opportunity rank</span>
                <Tooltip title={<OpportunityRankPopoverText/>}>
                    <InfoOutlined fontSize="small" color="action" aria-label="About opportunity rank"/>
                </Tooltip>
            </Box>,
            renderCell: p => <OpportunityRankCell rank={p.value as number}/>,
        },
        currencyColumn('spend', 'Spend'),
    ];
}

export function supplierBreakdownColumns(ctx: ColumnContext): GridColDef[] {
    const showSupplier = !ctx.byParent;
    const columns: (GridColDef | false)[] = [
        !ctx.hideParents && {
            field: 'sp_name', headerName: ctx.parentLabel, flex: 1, minWidth: 140,
            valueFormatter: (v: string | null) => v || '—',
        },
        showSupplier && {
            field: 's_name', headerName: 'Supplier name', flex: 1.2, minWidth: 180,
            renderCell: p => <Typography variant="body2" fontWeight={600} noWrap title={p.value as string}>{p.value as string}</Typography>,
        },
        showSupplier && ctx.showSupplierId && {
            field: 's_id', headerName: 'Supplier ID', width: 130,
            valueGetter: (_v, row) => getSupplierIdHack(row),
        },
        showSupplier && {
            field: 's_country_code', headerName: 'Country', width: 90,
            renderCell: p => p.value ? <CountryFlag code={p.value as string}/> : null,
        },
        ctx.showBusinessUnits && {
            field: 'business_units', headerName: ctx.buLabel, flex: 1, minWidth: 160, sortable: false,
            valueFormatter: (v: string[] | null) => Array.isArray(v) ? v.join(', ') : (v ?? ''),
            renderCell: p => {
                const text = Array.isArray(p.value) ? (p.value as string[]).join(', ') : (p.value as string ?? '');
                return <Typography variant="body2" noWrap title={text}>{text}</Typography>;
            },
        },
        ctx.showCDescription && {field: 'c_description', headerName: ctx.cDescriptionLabel, flex: 1, minWidth: 140},
        ...categoryColumns(ctx, true),
        ctx.showSource && {
            field: 'p_src_databag_id', headerName: 'Source', width: 140,
            valueFormatter: (v: number) => v ? ctx.getBagName(v) : '',
        },
        ctx.showDatasetName && {field: 'databag_name', headerName: 'Dataset name', width: 160},
        showSupplier && {
            field: 'context_menu_action', headerName: '', width: 56, sortable: false, disableColumnMenu: true,
            renderCell: p => <Box onClick={e => e.stopPropagation()}>
                <ContextMenuAction data={p.row as AdvancedSupplierBreakdownData}/>
            </Box>,
        },
        currencyColumn('total_spend', ctx.isSynergy ? 'Supplier category spend' : 'Total spend'),
        ctx.isSynergy && currencyColumn('parent_spend', 'Total spend'),
    ];
    return columns.filter((c): c is GridColDef => Boolean(c));
}

export function businessUnitPivotColumns(ctx: ColumnContext, pivotUnits: BusinessUnit[], rows: Record<string, unknown>[]): GridColDef[] {
    const base: (GridColDef | false)[] = [
        !ctx.hideParents && {
            field: 'sp_name', headerName: ctx.parentLabel, flex: 1, minWidth: 140, sortable: false,
            valueFormatter: (v: string | null) => v || '—',
        },
        !ctx.byParent && {
            field: 's_name', headerName: 'Supplier name', flex: 1.2, minWidth: 180, sortable: false,
            renderCell: p => <Typography variant="body2" fontWeight={600} noWrap title={p.value as string}>{p.value as string}</Typography>,
        },
        ...categoryColumns(ctx, false),
    ];
    const pivot: GridColDef[] = pivotUnits.map(bu => {
        const field = `spend_bu_${bu.id}`;
        const max = Math.max(0, ...rows.map(r => Math.abs(Number(r[field] ?? 0))));
        return {
            field, headerName: bu.bu_name, type: 'number', width: 140, align: 'right', headerAlign: 'right',
            renderCell: p => <SpendBarCell value={p.value as number} max={max}/>,
        };
    });
    return [...base.filter((c): c is GridColDef => Boolean(c)), ...pivot];
}
